import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCarSubsidiariesApi } from "../../http/carSubsidiaries/api";
import { CarSubsidiary, CarSubsidiaryNoCarDepency, OpenEntities } from "../../http/carSubsidiaries/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { NestedPick } from "../../types";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";
import OpenEntitiesDialog from "./OpenEntities";

const SUBSIDIARY_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARY_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

const CAR_SUBSIDIARY_FIELDS = [
  "id",
  "car.id",
  "startDate",
  "endDate",
  "subsidiary.id",
  "subsidiary.name",
  "versionNumber",
] as const;
type CarSubsidiaryField = (typeof CAR_SUBSIDIARY_FIELDS)[number];
export type CarSubsidiarySearch = NestedPick<CarSubsidiaryNoCarDepency, CarSubsidiaryField>;

export interface CarSubsidiaryFormBody {
  id: string;
  subsidiary: SubsidiarySearch;
  startDate: string;
  endDate?: string;
  prevEndDate?: string;
}

type CarSubsidiaryFormProps = {
  carSubsidiary?: CarSubsidiary | null;
  onSubmit: (form: CarSubsidiaryFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  currentCarSubsidiary?: CarSubsidiarySearch;
  onCloseEntitiesDialog: () => void;
  // allCarSubsidiaries: CarSubsidiary[];
};

const CarSubsidiaryForm: FC<CarSubsidiaryFormProps> = ({
  carSubsidiary,
  onSubmit,
  isDisabled,
  isLoading,
  currentCarSubsidiary,
  onCloseEntitiesDialog,
}: // allCarSubsidiaries,
CarSubsidiaryFormProps) => {
  const i18n = useI18n();
  const subsidiariesApi = useSubsidiariesApi();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const carSubsidiariesApi = useCarSubsidiariesApi();
  const [openEntitiesDialog, setOpenEntitiesDialog] = useState<{
    entites: OpenEntities | undefined;
    formData: CarSubsidiaryFormBody | undefined;
  }>({ entites: undefined, formData: undefined });

  const result = carSubsidiariesApi.useCarSubsidiaries(
    { orderBy: "carSubsidiary.endDate", desc: true, fields: CAR_SUBSIDIARY_FIELDS },
    aborter.current.signal,
    carSubsidiary?.car.id,
  );
  const [data, isInitialized] = useDataInitializer(result?.data, result?.error);

  useEffect(() => {
    result?.error && dispatch(showErrorNotification(i18n, result?.error));
  }, [result?.error]);

  const [defaultSubsidiaryOptions, setDefaultSubsidiaryOptions] = useState<SubsidiarySearch[]>([]);

  const defaultValues = {
    subsidiary: carSubsidiary?.subsidiary,
    startDate: carSubsidiary?.startDate,
    endDate: carSubsidiary?.endDate,
  };

  const getEntities = async (formData: CarSubsidiaryFormBody) => {
    if (formData.endDate && carSubsidiary) {
      const result = await carSubsidiariesApi.getOpenEntities(
        carSubsidiary?.id,
        formData.endDate!,
        aborter.current.signal,
      );

      if (result && hasAny(result)) {
        setOpenEntitiesDialog({ entites: result, formData: formData });
        return true;
      }
    }
    if (formData.prevEndDate && currentCarSubsidiary) {
      const result = await carSubsidiariesApi.getOpenEntities(
        currentCarSubsidiary.id,
        formData.prevEndDate!,
        aborter.current.signal,
      );

      if (result && hasAny(result)) {
        setOpenEntitiesDialog({ entites: result, formData: formData });
        return true;
      }
    }
    return false;
  };

  const handleSubmit = async (formData: CarSubsidiaryFormBody) => {
    const newStart = new Date(formData.startDate);
    const newEnd = formData.endDate ? new Date(formData.endDate) : null;

    const periodsToCheck = carSubsidiary ? data?.items.filter((period) => period.id !== carSubsidiary.id) : data?.items;
    const sortedPeriods = [...(periodsToCheck || [])].sort((a, b) => a.startDate.localeCompare(b.startDate));

    for (const period of sortedPeriods) {
      const periodStart = new Date(period.startDate);
      const periodEnd = formData.prevEndDate
        ? new Date(formData.prevEndDate)
        : period.endDate
          ? new Date(period.endDate)
          : null;

      if (
        (newEnd && periodEnd && newStart < periodEnd && newEnd > periodStart) ||
        (!newEnd && periodEnd && newStart < periodEnd) ||
        (newEnd && !periodEnd && newEnd > periodStart) ||
        (!newEnd && !periodEnd)
      ) {
        dispatch(showErrorNotification(i18n, undefined, i18n.t.carSubsidiaries.errors.overlapPeriodCarSubsidiary));
        return;
      }
    }

    const isOpen = await getEntities(formData);
    if (!isOpen) {
      await onSubmit(formData);
    }
  };

  return (
    <>
      <Form<CarSubsidiaryFormBody>
        defaultValues={defaultValues}
        submitText={i18n.t.common.save}
        disabled={isDisabled || (!!carSubsidiary && !isInitialized)}
        onSubmit={() => (formData) => handleSubmit(formData)}
        itemCreationInfo={carSubsidiary}
      >
        {({ control, reset, watch, setValue, getValues }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [carSubsidiary]);

          const prevStartDate = currentCarSubsidiary?.startDate;
          const startDate = watch("startDate");

          // if startDate is before the already assigned subsidiary, the endDate is required
          const endDateRequired = currentCarSubsidiary && startDate < currentCarSubsidiary?.startDate;

          // if there is an already assigned subsidiary and the endDate is not required, the toDate is required
          const toDateRequired = !!currentCarSubsidiary && !endDateRequired;

          useEffect(() => {
            if (!!toDateRequired) {
              setValue("prevEndDate", startDate);
            } else {
              setValue("prevEndDate", undefined);
            }
          }, [toDateRequired, startDate]);

          return (
            <FormFieldsContainer>
              {!!currentCarSubsidiary && (
                <>
                  <FormField label={i18n.t.subsidiaries.singular}>
                    <Input type="text" disabled value={currentCarSubsidiary?.subsidiary.name} />
                  </FormField>
                  <FormField label={i18n.t.common.from}>
                    <Input disabled value={currentCarSubsidiary?.startDate ?? ""} />
                  </FormField>
                  <Controller
                    name="prevEndDate"
                    control={control}
                    rules={{
                      required: !!toDateRequired,
                      validate: (value) => {
                        if (value && currentCarSubsidiary) {
                          if (value === currentCarSubsidiary.startDate) {
                            return i18n.t.common.endDateCantMatchStartDate;
                          }
                          if (value < currentCarSubsidiary.startDate) {
                            return i18n.t.common.endDateBeforeStartDate;
                          }
                        }
                        return undefined;
                      },
                    }}
                    render={({ fieldState }) => (
                      <FormField label={i18n.t.common.to} error={fieldState.error} isOptional={!toDateRequired}>
                        {({ isInvalid }) => (
                          <DatePicker
                            onChange={(v) => setValue("prevEndDate", v!)}
                            defaultValue={currentCarSubsidiary?.endDate || undefined}
                            value={!toDateRequired ? startDate : watch("prevEndDate")}
                            disabled={!!toDateRequired}
                            isInvalid={isInvalid}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </>
              )}
              <Controller
                name="subsidiary"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<SubsidiarySearch, SubsidiaryField>(
                        aborter,
                        searchTerm,
                        subsidiariesApi.getAdminSubsidiaries,
                        { fields: SUBSIDIARY_FIELDS },
                      );

                      // Fetch initial data on mount
                      useEffect(() => {
                        loadOptions(searchTerm.current).then((value) => {
                          setDefaultSubsidiaryOptions(value);
                          if (value.length === 1) {
                            const sub = value[0];
                            sub && setValue("subsidiary", sub);
                          }
                        });
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading || defaultSubsidiaryOptions.length === 1}
                          isLoading={isLoading}
                          defaultOptions={defaultSubsidiaryOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.name}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
              <Controller
                name="startDate"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    if (value) {
                      const prevEndDate = getValues("prevEndDate");
                      if (prevStartDate) {
                        if (prevEndDate) {
                          if (value >= prevStartDate && value < prevEndDate) {
                            return i18n.t.common.startDateFallsInCurrentAssignmentPeriod;
                          }
                        }
                      }
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.startDate} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        isInvalid={isInvalid}
                        disabled={isDisabled || isLoading}
                        value={watch("startDate")}
                        onChange={(v) => v && setValue("startDate", v)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="endDate"
                control={control}
                rules={{
                  required: !!endDateRequired,
                  validate: (value) => {
                    if (value) {
                      if (value === startDate) {
                        return i18n.t.common.endDateCantMatchStartDate;
                      }
                      if (value < startDate) {
                        return i18n.t.common.endDateBeforeStartDate;
                      }

                      const prevEndDate = watch("prevEndDate");
                      if (!prevEndDate && prevStartDate && value > prevStartDate) {
                        return i18n.t.common.endDateAfterCurrentAssignmentStartDate;
                      }
                      if (prevEndDate && prevStartDate && value > prevStartDate && value < prevEndDate) {
                        return i18n.t.common.endDateFallsInCurrentAssignmentPeriod;
                      }
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.endDate} error={fieldState.error} isOptional={!endDateRequired}>
                    {({ isInvalid }) => (
                      <DatePicker
                        isInvalid={isInvalid}
                        disabled={isDisabled || isLoading}
                        clearable={!endDateRequired}
                        value={watch("endDate")}
                        onChange={(v) => setValue("endDate", v!)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>

      {!!openEntitiesDialog && !!openEntitiesDialog.entites && !!openEntitiesDialog.formData && (
        <OpenEntitiesDialog
          onClose={() => setOpenEntitiesDialog({ formData: undefined, entites: undefined })}
          openEntities={openEntitiesDialog.entites}
          carSubsidiary={carSubsidiary}
          currentCarSubsidiary={currentCarSubsidiary}
          formData={openEntitiesDialog.formData}
          onUpdate={() => getEntities(openEntitiesDialog.formData!)}
          onCloseFormDialog={onCloseEntitiesDialog}
        />
      )}
    </>
  );
};

export default CarSubsidiaryForm;

function hasAny(result: OpenEntities): boolean {
  return Boolean(result.carDriver || result.otherAssignment || result.highwayPass || result.carPlate);
}
